/**
 * The big bang between the two hexagon sizes (amendment 17).
 *
 * Below zoom 12 the map shows the overview's res-7 cells and above it the res-9 ones (brief §14),
 * and the switch used to be a cut: one set of hexagons gone, the other there. Now zooming out
 * pulls the small cells into their big parent — faster and hotter the closer they get, until
 * they meet in a flash, a shock ring runs out and the parent springs from it — and zooming in
 * makes the parent flare, tremble and tear apart into its children, which fly out to where they
 * belong.
 *
 * A picture of numbers the map already has, like the pulse (amendment 14): the parent arrives
 * with the GDI the server rolled up for it, never one worked out here, and every piece has the
 * colour of the cell it is.
 *
 * A WebGL layer for the pulse's reason: every cell moves on every frame, and a MapLibre layer
 * would be tiled again for each one. It plays once per switch, a little over a second at the
 * full frame rate — drawing that somebody asked for by zooming — and then asks for nothing.
 * While it plays, the map's own hexagon layers hold back: `onReveal` tells them how far they may
 * show, 0 for the play and rising to 1 over its last stretch, where the pieces fade into them.
 *
 * Tilted (amendment 15), the pieces are columns: the small ones sink while they fall together
 * and the parent rises out of the flash; the other way round, the children grow as they fly.
 */

import { boundaryOf, type Cell, centreOf, parentOf } from '$domain/h3.ts';
import type { CustomLayerInterface, Map as MapLibreMap } from 'maplibre-gl';
import { columnMetres, liftFor } from './cell-motion.ts';
import { gdiRgb } from './gdi-scale.ts';
import type { CellCollection } from './types.ts';
import { anchored, buildProgram, glsl, mercator, type Point, vertexArray } from './webgl.ts';

/** The whole play in milliseconds: long enough to follow, short enough not to wait for. */
const DURATION = 1150;
/** When the falling cells meet in their parent, as a share of the play. */
const FUSE = 0.55;
/** When the parent tears apart, zooming the other way. */
const SPLIT = 0.22;
/** From here on the map's own hexagons take over from the pieces. */
const HANDOVER = 0.85;
/** A flash's square reaches this many cell radii from the bang: room for the ring to run. */
const QUAD = 1.5;
/** How opaque a piece is: a little more than the flat fill, and as much as a column tilted. */
const FLAT_ALPHA = 0.55;
const COLUMN_ALPHA = 0.62;
/** Where the light comes from, as for the columns: the north-west. */
const LIGHT: Point = { x: -0.6, y: -0.8 };
/** Past this many cells the play is skipped: a second of smear helps nobody. */
const MAX_CELLS = 1500;

/**
 * What a piece does. Zooming out, the children fall in (`infall`) and the parent is born out of
 * the flash; zooming in, the parent shatters and the children fly out as ejecta.
 */
const ROLE = { infall: 0, born: 1, shatter: 2, ejecta: 3, flash: 4, ring: 5 } as const;

/**
 * Floats per vertex: from (2), to (2), offset (2), up, normal (3), colour (3), height, role and
 * seed. A piece is a cell's column flying from `from` to `to`, with its corners as `offset`. A
 * flash or a ring is a square round a bang, whose `offset` is a corner of the unit square and
 * whose `height` is the cell's radius.
 */
const STRIDE = 16;

const VERTEX = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
uniform float u_t;
uniform float u_bang;
uniform float u_lift;
layout(location = 0) in vec2 a_from;
layout(location = 1) in vec2 a_to;
layout(location = 2) in vec2 a_offset;
layout(location = 3) in float a_up;
layout(location = 4) in vec3 a_normal;
layout(location = 5) in vec3 a_colour;
layout(location = 6) in float a_height;
layout(location = 7) in float a_role;
layout(location = 8) in float a_seed;
out vec3 v_colour;
out vec2 v_uv;
out float v_alpha;
out float v_glow;
out float v_up;
out float v_top;
out float v_rim;
out float v_size;
flat out int v_role;
const float PI = 3.14159265;
const float HANDOVER = ${glsl(HANDOVER)};
const vec2 LIGHT = vec2(${glsl(LIGHT.x)}, ${glsl(LIGHT.y)});
float settle(float x) { return clamp(x, 0.0, 1.0); }
// Gathering speed, as something falls.
float easeIn(float x) { return x * x * x; }
// Losing it, as something lands.
float easeOut(float x) { float y = 1.0 - x; return 1.0 - y * y * y; }
// Past the mark by a tenth, and back: what makes a landing look like one.
float overshoot(float x) { float y = x - 1.0; return 1.0 + 2.70158 * y * y * y + 1.70158 * y * y; }
vec2 turn(vec2 v, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return vec2(c * v.x - s * v.y, s * v.x + c * v.y);
}
void main() {
  int role = int(a_role + 0.5);
  v_role = role;
  v_colour = a_colour;
  v_uv = vec2(0.0);
  v_glow = 0.0;
  v_up = a_up;
  v_top = a_normal.z;
  v_rim = 0.0;
  v_size = 0.0;

  if (role >= ${ROLE.flash}) {
    // A square on the ground round the bang; the fragment shader cuts the shape out of it.
    float since = u_t - u_bang;
    bool flash = role == ${ROLE.flash};
    // The ring runs out within the play however late the bang: a ring cut off at the end pops.
    float k = settle(since / (flash ? 0.3 : 0.42));
    v_alpha = since < 0.0 ? 0.0 : (flash ? (1.0 - k) * (1.0 - k) : 1.0 - k);
    v_size = flash ? 0.3 + 0.9 * easeOut(k) : 0.15 + 1.2 * easeOut(k);
    v_uv = a_offset * ${glsl(QUAD)};
    gl_Position = u_matrix * vec4(a_to + a_offset * ${glsl(QUAD)} * a_height, 0.0, 1.0);
    return;
  }

  // Every piece turns a little while it flies, each its own way.
  float spin = (a_seed - 0.5) * 2.4;
  vec2 centre = a_to;
  float scale = 1.0;
  float angle = 0.0;
  float rise = 1.0;
  float alpha = 1.0;
  if (role == ${ROLE.infall}) {
    // Falling into the parent, the last of them arriving at the bang.
    float k = settle((u_t - 0.1 * a_seed) / (u_bang - 0.1));
    float e = easeIn(k);
    centre = mix(a_from, a_to, e);
    scale = mix(1.0, 0.3, e);
    angle = spin * e;
    rise = 1.0 - e;
    v_glow = e;
    alpha = 1.0 - smoothstep(0.85, 1.0, k);
  } else if (role == ${ROLE.born}) {
    // Out of the flash, overshooting, settling as the map's own hexagon takes over.
    float k = settle((u_t - u_bang) / (HANDOVER - u_bang));
    scale = overshoot(k);
    rise = scale;
    v_glow = 1.0 - k;
    alpha = u_t < u_bang ? 0.0 : 1.0;
  } else if (role == ${ROLE.shatter}) {
    // Heating up and trembling until the bang, then gone in a blink.
    float charge = settle(u_t / u_bang);
    float k = settle((u_t - u_bang) / 0.12);
    scale = (1.0 + 0.07 * sin(charge * PI * 4.0) * charge) * (1.0 - k);
    rise = 1.0 - k;
    v_glow = charge;
    alpha = 1.0 - k;
  } else {
    // Flung out from the parent's middle, cooling as they fly.
    float k = settle((u_t - u_bang - 0.06 * a_seed) / (HANDOVER - u_bang - 0.06));
    centre = mix(a_from, a_to, overshoot(k));
    scale = mix(0.25, 1.0, easeOut(k));
    angle = spin * (1.0 - easeOut(k));
    rise = easeOut(k);
    v_glow = 1.0 - k;
    alpha = u_t < u_bang ? 0.0 : 1.0;
  }

  vec2 normal = turn(a_normal.xy, angle);
  float shade = a_normal.z > 0.5 ? 1.0 : 0.6 + 0.4 * max(0.0, dot(normal, LIGHT));
  v_colour = a_colour * shade;
  v_alpha = alpha;
  // 0 in the middle of the top and 1 at its corners: the bright edge, like the flat outline.
  v_rim = a_normal.z > 0.5 && dot(a_offset, a_offset) > 0.0 ? 1.0 : 0.0;
  float height = a_height * u_lift * max(rise, 0.0);
  gl_Position = u_matrix * vec4(centre + turn(a_offset, angle) * scale, a_up * height, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform float u_lift;
uniform float u_fade;
uniform float u_dark;
in vec3 v_colour;
in vec2 v_uv;
in float v_alpha;
in float v_glow;
in float v_up;
in float v_top;
in float v_rim;
in float v_size;
flat in int v_role;
out vec4 fragColour;
void main() {
  if (v_role >= ${ROLE.flash}) {
    float r = length(v_uv);
    float shape = v_role == ${ROLE.flash}
      ? 1.0 - smoothstep(0.0, v_size, r)
      : 1.0 - smoothstep(0.0, 0.09, abs(r - v_size));
    float a = shape * v_alpha;
    if (a <= 0.004) discard;
    // On the dark map the light adds up; on the light one it is laid over, as the pulse's is.
    fragColour = vec4(mix(v_colour, vec3(1.0), 0.6) * a, a * (1.0 - u_dark));
    return;
  }
  vec3 colour = v_colour;
  // Walls fade towards the ground, as the columns' do.
  if (v_top < 0.5) colour *= 0.65 + 0.35 * v_up;
  float rim = v_top > 0.5 ? smoothstep(0.8, 1.0, v_rim) : 0.0;
  colour = mix(colour, vec3(1.0), clamp(0.75 * v_glow + 0.45 * rim, 0.0, 1.0));
  float a = v_alpha * (1.0 - u_fade);
  a *= mix(${glsl(FLAT_ALPHA)}, ${glsl(COLUMN_ALPHA)}, u_lift) + 0.3 * rim;
  // Not even depth for what cannot be seen: a spent piece must not hide the one behind it.
  if (a <= 0.004) discard;
  fragColour = vec4(colour * a, a);
}`;

type Geometry = {
  vertices: Float32Array;
  /** Vertices of the flying pieces, then of the flashes and rings after them. */
  pieces: number;
  lights: number;
  anchor: Point;
  /** When the bang happens, as a share of the play. */
  bang: number;
};

/** A number in [0, 1) that is always the same for a cell: how it spins, when it lands. */
function seedOf(cell: Cell): number {
  let hash = 2166136261;
  for (let index = 0; index < cell.length; index += 1) {
    hash ^= cell.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 1000) / 1000;
}

/** A cell's corners relative to its own middle, in mercator units, and how long a metre is. */
function shapeOf(cell: Cell): { corners: Point[]; metre: number } {
  const at = mercator(centreOf(cell));
  const corners = boundaryOf(cell)
    .slice(0, -1)
    .map(([lon, lat]) => {
      const corner = mercator({ lat, lon });
      return { x: corner.x - at.x, y: corner.y - at.y };
    });
  return { corners, metre: at.metre };
}

function geometryOf(from: CellCollection, to: CellCollection): Geometry | undefined {
  const fusion = from.resolution > to.resolution;
  const small = fusion ? from : to;
  const big = fusion ? to : from;
  if (small.features.length === 0 || big.features.length === 0) return undefined;
  if (small.features.length + big.features.length > MAX_CELLS) return undefined;
  const first = big.features[0];
  if (first === undefined) return undefined;

  const anchor = mercator(centreOf(first.properties.cell));
  const local = (cell: Cell): Point => {
    const at = mercator(centreOf(cell));
    return { x: at.x - anchor.x, y: at.y - anchor.y };
  };
  const out: number[] = [];

  /** A cell's column, as `cell-columns.ts` builds it, flying from one middle to another. */
  const piece = (cell: Cell, gdi: number, start: Point, end: Point, role: number) => {
    const { corners, metre } = shapeOf(cell);
    const colour = gdiRgb(gdi);
    const height = columnMetres(cell, gdi) * metre;
    const seed = seedOf(cell);
    const vertex = (offset: Point, up: number, normal: [number, number, number]) => {
      out.push(start.x, start.y, end.x, end.y, offset.x, offset.y, up, ...normal);
      out.push(...colour, height, role, seed);
    };
    corners.forEach((corner, index) => {
      const next = corners[(index + 1) % corners.length] ?? corner;
      vertex({ x: 0, y: 0 }, 1, [0, 0, 1]);
      vertex(corner, 1, [0, 0, 1]);
      vertex(next, 1, [0, 0, 1]);
      const ex = next.x - corner.x;
      const ey = next.y - corner.y;
      const length = Math.hypot(ex, ey) || 1;
      let normal: [number, number, number] = [ey / length, -ex / length, 0];
      if (normal[0] * (corner.x + next.x) + normal[1] * (corner.y + next.y) < 0) {
        normal = [-normal[0], -normal[1], 0];
      }
      vertex(corner, 0, normal);
      vertex(next, 0, normal);
      vertex(corner, 1, normal);
      vertex(next, 0, normal);
      vertex(next, 1, normal);
      vertex(corner, 1, normal);
    });
  };

  for (const { properties } of small.features) {
    const own = local(properties.cell);
    const parent = local(parentOf(properties.cell, big.resolution));
    if (fusion) piece(properties.cell, properties.gdi, own, parent, ROLE.infall);
    else piece(properties.cell, properties.gdi, parent, own, ROLE.ejecta);
  }
  for (const { properties } of big.features) {
    const own = local(properties.cell);
    piece(properties.cell, properties.gdi, own, own, fusion ? ROLE.born : ROLE.shatter);
  }
  const pieces = out.length / STRIDE;

  // A flash and a shock ring where every big cell is: born there, or torn apart there.
  for (const { properties } of big.features) {
    const own = local(properties.cell);
    const corner = shapeOf(properties.cell).corners[0] ?? { x: 0, y: 0 };
    const radius = Math.hypot(corner.x, corner.y);
    const colour = gdiRgb(properties.gdi);
    for (const role of [ROLE.flash, ROLE.ring]) {
      const vertex = (u: number, v: number) => {
        out.push(own.x, own.y, own.x, own.y, u, v, 0, 0, 0, 1, ...colour, radius, role, 0);
      };
      vertex(-1, -1);
      vertex(1, -1);
      vertex(-1, 1);
      vertex(1, -1);
      vertex(1, 1);
      vertex(-1, 1);
    }
  }

  return {
    vertices: new Float32Array(out),
    pieces,
    lights: out.length / STRIDE - pieces,
    anchor,
    bang: fusion ? FUSE : SPLIT,
  };
}

type Gpu = {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  buffer: WebGLBuffer;
  uniform: (name: string) => WebGLUniformLocation | null;
};

export type CellBurst = {
  /** For `addLayer`: after the columns, so that a building or a column hides what is behind. */
  layer: CustomLayerInterface;
  /**
   * Play the switch from one set of cells to the other. False when there is nothing to play —
   * the same size twice, an empty side, motion reduced — and then the new cells simply show.
   */
  play(from: CellCollection, to: CellCollection): boolean;
  /** End a play at once: a newer answer overtook it, or the hexagons are no longer shown. */
  stop(): void;
  destroy(): void;
};

export function createCellBurst({
  id,
  dark,
  onReveal,
}: {
  id: string;
  dark: boolean;
  /** How far the map's own hexagons may show: 0 while the pieces fly, 1 once they are done. */
  onReveal: (share: number) => void;
}): CellBurst {
  let map: MapLibreMap | undefined;
  let gpu: Gpu | undefined;
  let broken = false;
  let geometry: Geometry | undefined;
  let uploaded = false;
  let started = 0;
  let frame: number | undefined;

  const motion =
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : undefined;

  const progress = () => Math.min(1, (performance.now() - started) / DURATION);
  const handover = (t: number) => Math.max(0, (t - HANDOVER) / (1 - HANDOVER));

  function finish() {
    if (frame !== undefined) cancelAnimationFrame(frame);
    frame = undefined;
    if (geometry === undefined) return;
    geometry = undefined;
    onReveal(1);
    map?.triggerRepaint();
  }

  // Every frame of the play, at the full rate: it is a second long and somebody is watching it.
  function tick() {
    frame = undefined;
    if (geometry === undefined) return;
    const t = progress();
    if (t >= 1 || document.visibilityState !== 'visible') {
      finish();
      return;
    }
    onReveal(handover(t));
    map?.triggerRepaint();
    frame = requestAnimationFrame(tick);
  }

  /** As in `swarm-pulse.ts`: made on the first frame, and a failure costs the play only. */
  function prepare(gl: WebGL2RenderingContext) {
    if (gpu !== undefined || broken) return gpu;
    try {
      const program = buildProgram(gl, VERTEX, FRAGMENT);
      const buffer = gl.createBuffer();
      if (buffer === null) throw new Error('no buffer');
      const vao = vertexArray(
        gl,
        buffer,
        [
          [0, 2, 0],
          [1, 2, 2],
          [2, 2, 4],
          [3, 1, 6],
          [4, 3, 7],
          [5, 3, 10],
          [6, 1, 13],
          [7, 1, 14],
          [8, 1, 15],
        ],
        STRIDE,
      );
      const uniforms = new Map<string, WebGLUniformLocation | null>();
      gpu = {
        program,
        vao,
        buffer,
        uniform: (name) => {
          if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
          return uniforms.get(name) ?? null;
        },
      };
      uploaded = false;
    } catch (error) {
      broken = true;
      console.error('cell burst disabled', error);
    }
    return gpu;
  }

  const layer: CustomLayerInterface = {
    id,
    type: 'custom',
    renderingMode: '3d',
    onAdd(added) {
      map = added;
    },
    onRemove(_removed, gl) {
      if (gpu !== undefined) {
        gl.deleteProgram(gpu.program);
        gl.deleteVertexArray(gpu.vao);
        gl.deleteBuffer(gpu.buffer);
      }
      gpu = undefined;
      map = undefined;
    },
    render(gl, options) {
      if (map === undefined || geometry === undefined) return;
      const projection = map.getProjection()?.type;
      if (projection !== undefined && projection !== 'mercator') return;
      const ready = prepare(gl);
      if (ready === undefined) {
        // Nothing to show the switch with: let the hexagons back at once rather than leave a
        // second of empty map. Not from inside a frame — that would restyle the map mid-draw.
        setTimeout(finish);
        return;
      }

      gl.bindVertexArray(ready.vao);
      if (!uploaded) {
        gl.bindBuffer(gl.ARRAY_BUFFER, ready.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, geometry.vertices, gl.STATIC_DRAW);
        uploaded = true;
      }
      const t = progress();
      gl.useProgram(ready.program);
      gl.uniformMatrix4fv(
        ready.uniform('u_matrix'),
        false,
        anchored(options.defaultProjectionData.mainMatrix, geometry.anchor),
      );
      gl.uniform1f(ready.uniform('u_t'), t);
      gl.uniform1f(ready.uniform('u_bang'), geometry.bang);
      gl.uniform1f(ready.uniform('u_lift'), liftFor(map.getPitch()));
      gl.uniform1f(ready.uniform('u_fade'), handover(t));
      gl.uniform1f(ready.uniform('u_dark'), dark ? 1 : 0);
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);

      // The pieces as the columns are drawn: the depth of the nearest faces first, then their
      // colour where they are nearest, so a translucent column does not show its own back.
      gl.disable(gl.BLEND);
      gl.colorMask(false, false, false, false);
      gl.depthMask(true);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.pieces);
      gl.colorMask(true, true, true, true);
      gl.depthMask(false);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.pieces);

      // The flashes and rings: tested against what stands in front, written to nothing.
      gl.drawArrays(gl.TRIANGLES, geometry.pieces, geometry.lights);
      gl.bindVertexArray(null);
    },
  };

  return {
    layer,
    play(from, to) {
      finish();
      if (map === undefined || from.resolution === to.resolution) return false;
      if (motion?.matches === true || document.visibilityState !== 'visible') return false;
      const built = geometryOf(from, to);
      if (built === undefined) return false;
      geometry = built;
      uploaded = false;
      started = performance.now();
      onReveal(0);
      map.triggerRepaint();
      frame = requestAnimationFrame(tick);
      return true;
    },
    stop: finish,
    destroy() {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
      geometry = undefined;
      map = undefined;
    },
  };
}
